import React from 'react';
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import styled from 'styled-components';
import { EuiIcon, EuiToolTip } from '@elastic/eui';

const ChartWrapper = styled.div`
  min-width: 0;

  h4 {
    margin: 0 0 14px;
    color: rgba(255, 255, 255, 0.92);
    display: flex;
    align-items: center;
    gap: 6px;
  }
`;

const TooltipWrapper = styled.div`
  padding: 10px 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  background: rgba(20, 21, 26, 0.96);

  p {
    margin: 3px 0;
    font-size: 12px;
  }

  .time {
    margin-bottom: 7px;
    color: rgba(255, 255, 255, 0.55);
  }
`;

const UsageChart = ({ title, data, series, unit, description }) => (
    <ChartWrapper>
        <h4>
            <span>{ title }</span>
            { description && (
                <EuiToolTip position="top" content={ description }>
                    <EuiIcon 
                        type="questionInCircle" 
                        size="s" 
                        style={{ 
                            opacity: 0.5, 
                            cursor: 'help',
                            verticalAlign: 'middle' 
                        }} 
                    />
                </EuiToolTip>
            ) }
        </h4>
        <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={ data } margin={{ top: 8, right: 8, left: -14, bottom: 0 }}>
                <defs>
                    { series.map(item => (
                        <linearGradient
                            id={ item.gradientId }
                            key={ item.gradientId }
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                        >
                            <stop offset="5%" stopColor={ item.color } stopOpacity={ 0.28 }/>
                            <stop offset="95%" stopColor={ item.color } stopOpacity={ 0 }/>
                        </linearGradient>
                    )) }
                </defs>
                <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(255,255,255,0.04)"
                    vertical={ false }
                />
                <XAxis
                    dataKey="name"
                    stroke="rgba(255,255,255,0.35)"
                    fontSize={ 9 }
                    tickLine={ false }
                    axisLine={ false }
                    minTickGap={ 24 }
                />
                <YAxis
                    stroke="rgba(255,255,255,0.35)"
                    fontSize={ 9 }
                    tickLine={ false }
                    axisLine={ false }
                    unit={ unit }
                />
                <Tooltip content={ <UsageTooltip unit={ unit }/> }/>
                { series.map(item => (
                    <Area
                        type="monotone"
                        key={ item.key }
                        dataKey={ item.key }
                        name={ item.label }
                        stroke={ item.color }
                        strokeWidth={ 2 }
                        fill={ `url(#${ item.gradientId })` }
                        activeDot={{ r: 4, strokeWidth: 0 }}
                    />
                )) }
            </AreaChart>
        </ResponsiveContainer>
    </ChartWrapper>
);

const UsageTooltip = ({ active, label, payload, unit }) => {
    if (!active || !payload || payload.length === 0) {
        return null;
    }

    return (
        <TooltipWrapper>
            <p className="time">{ label }</p>
            { payload.map(item => (
                <p key={ item.dataKey } style={{ color: item.color }}>
                    { item.name }: { item.value }{ unit }
                </p>
            )) }
        </TooltipWrapper>
    );
};

export default UsageChart;
